import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  type AvailableValueSource,
  type DetectedEnvironment,
  type DetectedVariable,
  type EffectiveEnvironment,
  type EffectiveVariable,
  type Environment,
  type FrontendEnvVarScope,
  type FrontendEnvVarVisibility,
  type ValueSource,
} from '../api';
import { valueSourceGroupLabel, valueSourceLabel } from '../labels';
import { Callout, Checkbox, Dialog, Icon, Input, Radio, RowActions, Select, Table } from './primitives';
import { ConfirmDialog, ErrorNotice, Field, LoadingRows, StatusBadge } from './ui';

/**
 * Environment variables of one deployable component in one environment
 * (CO-52).
 *
 * The Magento binding says *what* a storefront is connected to. This card
 * says *how* the repository receives it: the repository owns every variable
 * name, and each variable either holds a value the operator typed or takes
 * one from Commerce Ops — the store view code, the GraphQL endpoint, the
 * public URL — resolved fresh on every deployment.
 */
export function ComponentEnvironmentCard({
  projectId,
  applicationId,
  applicationName,
  environment,
  hideHeading = false,
}: {
  projectId: string;
  applicationId: string;
  applicationName: string;
  environment: Environment;
  hideHeading?: boolean;
}) {
  const [config, setConfig] = useState<EffectiveEnvironment | null>(null);
  const [sources, setSources] = useState<AvailableValueSource[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<EffectiveVariable | 'new' | null>(null);
  const [removing, setRemoving] = useState<EffectiveVariable | null>(null);
  const [detection, setDetection] = useState<DetectedEnvironment | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [env, src] = await Promise.all([
        api.getComponentEnvironment(environment.id, applicationId),
        api.getComponentValueSources(environment.id, applicationId),
      ]);
      setConfig(env.environment);
      setSources(src.sources);
      setError(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [environment.id, applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, message?: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (message) setNotice(message);
      await load();
      return true;
    } catch (e) {
      setError(e as Error);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const blocking = config?.issues.filter((i) => i.severity === 'blocking') ?? [];
  const warnings = config?.issues.filter((i) => i.severity === 'warning') ?? [];

  return (
    <section className="card env-vars" aria-labelledby={`env-vars-${applicationId}`} data-testid="component-environment">
      <div className="readiness__head">
        {hideHeading ? null : (
          <div>
            <h3 id={`env-vars-${applicationId}`}>
              Environment variables
            </h3>
          </div>
        )}
        <div className="inline-form">
          <button
            type="button"
            className="button button--secondary button--small"
            onClick={() =>
              void run('detect', async () => {
                const res = await api.detectComponentEnvironment(environment.id, applicationId);
                setDetection(res.detection);
              })
            }
            disabled={busy !== null}
            data-testid="detect-variables"
          >
            {busy === 'detect' ? 'Reading repository' : 'Detect from repository'}
          </button>
          <button type="button" className="button button--secondary button--small" onClick={() => setEditing('new')} disabled={busy !== null}>
            Add variable
          </button>
        </div>
      </div>

      <ErrorNotice error={error} />
      {notice ? (
        <Callout tone="success">{notice}</Callout>
      ) : null}

      {blocking.length > 0 ? (
        <Callout tone="danger" title={`${applicationName} cannot be deployed to ${environment.name} yet`} testId="env-blocking">
          <ul className="callout__list">
            {blocking.map((i, idx) => (
              <li key={`${i.code}-${idx}`}>{i.message}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {warnings.length > 0 ? (
        <Callout tone="warning" testId="env-warnings">
          <ul className="callout__list">
            {warnings.map((i, idx) => (
              <li key={`${i.code}-${idx}`}>{i.message}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {loading && !config ? (
        <LoadingRows label="Loading variables…" rows={3} rowHeight="40px" />
      ) : !config || config.variables.length === 0 ? (
        <p className="quiet-empty">No variables</p>
      ) : (
        <Table label="Environment variables" compact>
          <thead>
            <tr>
              <th scope="col">Variable</th>
              <th scope="col">Value</th>
              <th scope="col">Build</th>
              <th scope="col">Runtime</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {config.variables.map((v) => (
              <tr key={v.id} data-testid={`variable-row-${v.name}`}>
                <td>
                  <code translate="no">{v.name}</code>
                  <div className="table__sub">
                    {v.source_label}
                    {v.required ? ' · required' : ''}
                  </div>
                </td>
                <td>
                  <ValueCell variable={v} />
                </td>
                <td>{v.build_time ? <span aria-label="Used at build time"><Icon name="check" /></span> : <span className="muted" aria-label="Not used at build time">—</span>}</td>
                <td>{v.runtime ? <span aria-label="Used at runtime"><Icon name="check" /></span> : <span className="muted" aria-label="Not used at runtime">—</span>}</td>
                <td>
                  <RowActions>
                    <button type="button" className="button button--secondary button--small" onClick={() => setEditing(v)} aria-label={`Edit ${v.name}`}>
                      Edit
                    </button>
                    <button type="button" className="button button--danger-quiet button--small" onClick={() => setRemoving(v)} aria-label={`Remove ${v.name}`}>
                      Remove
                    </button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {config && config.variables.length > 0 ? <EffectivePreview config={config} /> : null}
      {config?.framework_note ? (
        <p className="type-meta">
          {config.framework_note}
        </p>
      ) : null}

      {editing ? (
        <VariableDialog
          key={editing === 'new' ? 'new' : editing.id}
          variable={editing === 'new' ? null : editing}
          sources={sources}
          environmentName={environment.name}
          busy={busy === 'save'}
          error={null}
          onClose={() => setEditing(null)}
          onSave={async (input) => {
            const ok = await run(
              'save',
              () =>
                api.setFrontendEnvVar(projectId, applicationId, {
                  environmentId: environment.id,
                  name: input.name,
                  value: input.value,
                  source: input.source,
                  scope: input.scope,
                  visibility: input.visibility,
                  required: input.required,
                }),
              `${input.name} saved. It takes effect with the next deployment of ${environment.name}.`,
            );
            if (ok) setEditing(null);
          }}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          open
          title={`Remove ${removing.name} from ${environment.name}?`}
          description={
            removing.visibility === 'secret'
              ? `The next build and restart of ${environment.name} runs without it. The stored secret cannot be recovered afterwards.`
              : `The next build and restart of ${environment.name} runs without it. You can add it again later.`
          }
          confirmLabel="Remove variable"
          danger
          busy={busy === 'delete'}
          onCancel={() => setRemoving(null)}
          onConfirm={() =>
            void run('delete', () => api.deleteFrontendEnvVar(projectId, applicationId, removing.id), `${removing.name} removed.`).then(() =>
              setRemoving(null),
            )
          }
        />
      ) : null}

      {detection ? (
        <DetectionDialog
          detection={detection}
          componentName={applicationName}
          busy={busy === 'import'}
          onClose={() => setDetection(null)}
          onImport={async (variables) => {
            const ok = await run(
              'import',
              () => api.importComponentEnvironment(environment.id, applicationId, variables),
              `${variables.length} variable${variables.length === 1 ? '' : 's'} added from the repository.`,
            );
            if (ok) setDetection(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** ValueCell shows what a variable resolves to, or why it does not. */
function ValueCell({ variable }: { variable: EffectiveVariable }) {
  if (variable.visibility === 'secret') {
    return variable.resolved ? (
      <StatusBadge kind="variableValue" value="secret_stored" />
    ) : (
      <StatusBadge kind="variableValue" value="missing_secret" />
    );
  }
  if (!variable.resolved) {
    return (
      <span>
        <StatusBadge kind="variableValue" value="missing" />
        <span className="fact-block__sub">
          {variable.reason}
        </span>
      </span>
    );
  }
  return (
    <code translate="no" className="code--wrap">
      {variable.value}
    </code>
  );
}

/** EffectivePreview renders the configuration as the deployment will see it. */
function EffectivePreview({ config }: { config: EffectiveEnvironment }) {
  const text = useMemo(
    () =>
      config.variables
        .filter((v) => v.resolved)
        .map((v) => `${v.name}=${v.visibility === 'secret' ? '••••••••' : v.value}`)
        .join('\n'),
    [config],
  );
  return (
    <details className="topology__details" data-testid="effective-preview">
      <summary>Effective configuration in {config.environment_name}</summary>
      <div className="topology__section-body">
        <pre className="topology__pre" translate="no">
          {text || 'Nothing resolves yet.'}
        </pre>
      </div>
    </details>
  );
}

interface VariableInput {
  name: string;
  value: string;
  source: ValueSource;
  scope: FrontendEnvVarScope;
  visibility: FrontendEnvVarVisibility;
  required: boolean;
}

/**
 * VariableDialog edits one variable. The value is either typed here or
 * taken from Commerce Ops; choosing a Commerce Ops value shows what it
 * currently resolves to, so nobody has to guess.
 */
function VariableDialog({
  variable,
  sources,
  environmentName,
  busy,
  error,
  onClose,
  onSave,
}: {
  variable: EffectiveVariable | null;
  sources: AvailableValueSource[];
  environmentName: string;
  busy: boolean;
  error: Error | null;
  onClose: () => void;
  onSave: (input: VariableInput) => void;
}) {
  const [name, setName] = useState(variable?.name ?? '');
  const [kind, setKind] = useState<'literal' | 'commerceops'>(variable && variable.source !== 'literal' ? 'commerceops' : 'literal');
  const [source, setSource] = useState<ValueSource>(variable && variable.source !== 'literal' ? variable.source : '');
  const [value, setValue] = useState('');
  const [secret, setSecret] = useState((variable?.visibility ?? 'public') === 'secret');
  const [build, setBuild] = useState(variable ? variable.build_time : true);
  const [runtime, setRuntime] = useState(variable ? variable.runtime : true);
  const [required, setRequired] = useState(variable?.required ?? false);
  const [localError, setLocalError] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const groups: Record<string, AvailableValueSource[]> = {};
    for (const s of sources) {
      (groups[s.group] ??= []).push(s);
    }
    return groups;
  }, [sources]);
  const chosen = sources.find((s) => s.source === source);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setLocalError('Enter the variable name your repository expects.');
      return;
    }
    if (kind === 'commerceops' && !source) {
      setLocalError('Choose which Commerce Ops value this variable carries.');
      return;
    }
    if (kind === 'literal' && secret && !value.trim() && !variable?.resolved) {
      setLocalError('Enter the secret value. It is stored encrypted and never shown again.');
      return;
    }
    if (!build && !runtime) {
      setLocalError('A variable is needed at build time, at runtime, or both.');
      return;
    }
    setLocalError(null);
    onSave({
      name: name.trim(),
      value: kind === 'commerceops' ? '' : value,
      source: kind === 'commerceops' ? source : 'literal',
      scope: build && runtime ? 'build_and_runtime' : build ? 'build' : 'runtime',
      visibility: kind === 'commerceops' ? 'public' : secret ? 'secret' : 'public',
      required,
    });
  }

  return (
    <FormDialog
      title={variable ? `Edit ${variable.name}` : `Add a variable to ${environmentName}`}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={variable ? 'Save variable' : 'Add variable'}
      testId="variable-dialog"
      busy={busy}
      error={error}
      notice={localError}
    >
      <Field label="Variable name" hint="Exactly as the repository reads it. Commerce Ops never requires a prefix.">
        <Input
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          readOnly={!!variable}
          required
          autoComplete="off"
          spellCheck={false}
          placeholder="MAGENTO_STORE_VIEW"
          data-testid="variable-name"
        />
      </Field>

      <fieldset className="topology__fieldset">
        <legend>Value</legend>
        <Radio
          name="value-kind"
          checked={kind === 'commerceops'}
          onChange={() => setKind('commerceops')}
          data-testid="kind-commerceops"
          label="Commerce Ops value"
          hint="Filled in on every deployment, so it never goes stale."
        />
        {kind === 'commerceops' ? (
          <Field label="Which value" hint={chosen ? chosen.description : 'Only values Commerce Ops can resolve for this component are listed.'}>
            <Select value={source} onChange={(e) => setSource(e.target.value)} data-testid="variable-source" required>
              <option value="">Choose a value</option>
              {Object.entries(grouped).map(([group, list]) => (
                <optgroup key={group} label={valueSourceGroupLabel[group] ?? group}>
                  {list.map((s) => (
                    <option key={s.source} value={s.source}>
                      {s.label}
                      {s.resolved ? ` — ${s.current_value}` : ' — not available yet'}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </Field>
        ) : null}
        {chosen && kind === 'commerceops' && !chosen.resolved ? (
          <Callout tone="warning">{chosen.reason}</Callout>
        ) : null}

        <Radio
          name="value-kind"
          checked={kind === 'literal'}
          onChange={() => setKind('literal')}
          data-testid="kind-literal"
          label="Enter a value"
        />
        {kind === 'literal' ? (
          <>
            <Field label={secret ? (variable ? 'New secret value' : 'Secret value') : 'Value'} hint={secret ? 'Stored encrypted in the secret store and never shown again.' : undefined}>
              <Input
                name="value"
                type={secret ? 'password' : 'text'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                data-testid="variable-value"
              />
            </Field>
            <Checkbox
              checked={secret}
              onChange={(e) => setSecret(e.target.checked)}
              data-testid="variable-secret"
              label="This is a secret"
              hint="Kept in the secret store, masked everywhere, never written to a log or a deployment record."
            />
          </>
        ) : null}
      </fieldset>

      <fieldset className="topology__fieldset">
        <legend>When it is needed</legend>
        <Checkbox
          checked={build}
          onChange={(e) => setBuild(e.target.checked)}
          data-testid="variable-build"
          label="While building"
        />
        <Checkbox
          checked={runtime}
          onChange={(e) => setRuntime(e.target.checked)}
          data-testid="variable-runtime"
          label="While running"
        />
        <Checkbox
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
          data-testid="variable-required"
          label="Required"
          hint="A deployment stops before it starts when this has no value."
        />
      </fieldset>
    </FormDialog>
  );
}

/**
 * DetectionDialog reviews what the repository declares before anything is
 * written: every row is opt-in, and a credential is never imported with a
 * value from an example file.
 */
function DetectionDialog({
  detection,
  componentName,
  busy,
  onClose,
  onImport,
}: {
  detection: DetectedEnvironment;
  componentName: string;
  busy: boolean;
  onClose: () => void;
  onImport: (variables: { name: string; source?: ValueSource; value?: string; scope: FrontendEnvVarScope; visibility: FrontendEnvVarVisibility; required?: boolean }[]) => void;
}) {
  const importable = detection.variables.filter((v) => !v.configured && v.visibility !== 'secret');
  const [chosen, setChosen] = useState<Record<string, boolean>>(() => Object.fromEntries(importable.map((v) => [v.name, true])));

  function submit(e: FormEvent) {
    e.preventDefault();
    onImport(
      importable
        .filter((v) => chosen[v.name])
        .map((v) => ({
          name: v.name,
          source: v.recommendation.source ?? 'literal',
          value: '',
          scope: v.scope,
          visibility: 'public' as FrontendEnvVarVisibility,
          required: !!v.recommendation.source,
        })),
    );
  }

  const selectedCount = importable.filter((v) => chosen[v.name]).length;
  return (
    <FormDialog
      title={`What ${componentName}'s repository expects`}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={selectedCount === 0 ? 'Add nothing' : `Add ${selectedCount} variable${selectedCount === 1 ? '' : 's'}`}
      testId="detection-dialog"
      busy={busy}
      error={null}
      wide
    >
      {detection.reason ? (
        <Callout tone="warning">{detection.reason}</Callout>
      ) : null}
      {detection.variables.length === 0 ? null : (
        <>
          <p className="type-description">
            Read from {detection.files_read.join(', ')} · {detection.framework_label}
          </p>
          <Table label="Variables found in the repository" compact>
            <thead>
              <tr>
                <th scope="col">
                  <span className="visually-hidden">Add</span>
                </th>
                <th scope="col">Variable</th>
                <th scope="col">Value</th>
                <th scope="col">Found in</th>
              </tr>
            </thead>
            <tbody>
              {detection.variables.map((v) => (
                <tr key={v.name} data-testid={`detected-${v.name}`}>
                  <td>
                    {v.configured || v.visibility === 'secret' ? (
                      <span className="muted">—</span>
                    ) : (
                      <Input
                        type="checkbox"
                        checked={!!chosen[v.name]}
                        onChange={(e) => setChosen((c) => ({ ...c, [v.name]: e.target.checked }))}
                        aria-label={`Add ${v.name}`}
                      />
                    )}
                  </td>
                  <td>
                    <code translate="no">{v.name}</code>
                    <div className="table__sub">
                      {v.scope === 'runtime' ? 'runtime' : v.scope === 'build' ? 'build' : 'build and runtime'}
                    </div>
                  </td>
                  <td>
                    <DetectedValue variable={v} />
                  </td>
                  <td className="type-meta">
                    {v.found_in.join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </FormDialog>
  );
}

function DetectedValue({ variable }: { variable: DetectedVariable }) {
  if (variable.configured) {
    return <span className="muted">Already configured</span>;
  }
  if (variable.visibility === 'secret') {
    return <span className="muted">A credential: add it yourself and mark it secret</span>;
  }
  if (variable.recommendation.source) {
    return (
      <span>
        {valueSourceLabel(variable.recommendation.source)}
        {variable.recommendation.confidence === 'low' ? <span className="muted"> · check this</span> : null}
      </span>
    );
  }
  return (
    <span className="muted">
      Needs a value{variable.example_value ? ` · example: ${variable.example_value}` : ''}
    </span>
  );
}

/** FormDialog: a form in the shared Dialog (CO-91) with [Cancel] [Save]. */
function FormDialog({
  title,
  children,
  onClose,
  onSubmit,
  submitLabel,
  testId,
  busy = false,
  error = null,
  notice = null,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  submitLabel: string;
  testId?: string;
  busy?: boolean;
  error?: Error | null;
  notice?: string | null;
  wide?: boolean;
}) {
  return (
    <Dialog
      open
      size={wide ? 'lg' : 'md'}
      title={title}
      onClose={onClose}
      busy={busy}
      onSubmit={onSubmit}
      testId={testId}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={busy}>
            {busy ? 'Saving' : submitLabel}
          </button>
        </>
      }
    >
      {children}
      {notice ? <Callout tone="danger">{notice}</Callout> : null}
      <ErrorNotice error={error} />
    </Dialog>
  );
}
